import { ArrowUpIcon, DeleteIcon, MinusIcon, WarningTwoIcon } from '@chakra-ui/icons'
import {
  Box,
  Flex,
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
  Text,
} from '@chakra-ui/react'
import { PopUpMessageContext, useApp } from '@hb/react'
import {
  Assessment,
  ASSESSMENTS,
  capitalizeFirstLetter,
  CoverageId,
  getCoveragePath,
  getPlanNextAction,
  getPlanNextActionPath,
  pregnancyHasPlan,
  SetPregnancyCoverageLabelArgs,
  USERS_ADMIN,
  WithId,
} from '@hb/shared'
import { arrayRemove, deleteField, doc, writeBatch } from 'firebase/firestore'
import { useCallback, useContext, useMemo, useState } from 'react'
import { db } from '../../../backend/db/db'
import { makeCoveragePrimary, setPregnancyCoverageLabel } from '../../../backend/functions/other'
import { useProfile } from '../../../contexts/user/ProfileContext'
import { ActionButton, DeleteButton, SolidActionButton } from '../../Buttons'
import { NextActionPreview } from '../../Users/Profile/NextAction'
import { BasicPregnancyPreview } from '../AssessmentPreview'
import { useCoverageView } from './context'

// const messageField: TextAreaField = {
//   ...notesField,
//   optional: false,
//   placeholder: 'Message to patient',
// }

const DeleteFromPatientAlertBody = ({
  linkedPregnancies,
}: {
  linkedPregnancies: Array<WithId<Assessment>>
}) => (
  <Flex flexFlow="column" w="100%" gap={1}>
    {linkedPregnancies.length ? (
      <Text mb={0.5} fontWeight={600} lineHeight={1} color="red.600">
        Coverage cannot be deleted while linked to pregnancies
      </Text>
    ) : null}
    <Text mb={1} lineHeight={1} fontWeight={600} color="gray.600">
      Linked pregnancies
    </Text>
    {linkedPregnancies.length ? (
      linkedPregnancies.map(p => <BasicPregnancyPreview key={p.id} pregnancy={p} />)
    ) : (
      <Text lineHeight={1} color="gray.500">
        No linked pregnancies
      </Text>
    )}
  </Flex>
)

const UnlinkFromPregnancyAlertBody = () => {
  const { assessment, coverageId } = useCoverageView()
  const nextAction = useMemo(
    () => getPlanNextAction(assessment?.nextActions ?? {}, coverageId),
    [assessment, coverageId],
  )

  return (
    <Flex flexFlow="column" w="100%" gap={1}>
      <Text mb={0.5} lineHeight={1}>
        This will keep the coverage on the patient profile but unlink it from the pregnancy
      </Text>
      {nextAction ? (
        <Flex
          border="1px solid"
          borderColor="gray.200"
          borderRadius={4}
          bg="whiteAlpha.500"
          py={2}
          gap={1}
          px={3}
          flexFlow="column">
          <Flex gap={2} w="100%" align="center">
            <WarningTwoIcon w={5} h={5} color="yellow.500" />
            <Text>This coverage has a next action for this pregnancy</Text>
          </Flex>
          <NextActionPreview nextAction={nextAction} />
        </Flex>
      ) : null}
    </Flex>
  )
}

const PatientCoverageSetAs = () => {
  const { coverageId, patientRef } = useCoverageView()
  const { appName } = useApp()
  const { showMessage } = useContext(PopUpMessageContext)
  const [loading, setLoading] = useState(false)
  const isPrimary = useMemo(() => coverageId === 'primary', [coverageId])
  const isSecondary = useMemo(() => coverageId === 'secondary', [coverageId])
  const handleSetAs = useCallback(
    async (label: CoverageId | null) => {
      try {
        setLoading(true)
        const {
          data: { success },
        } = await makeCoveragePrimary({
          // assessmentId,
          appName,
          patientId: patientRef.id,
          coverageId,
          label: label ?? null,
          message: '',
          sendEmail: false,
        })
        showMessage({
          type: 'success',
          text: 'Coverage updated',
          subText: success,
        })
      } catch (err: any) {
        showMessage({
          type: 'error',
          text: `Error ${isSecondary ? 'unsetting' : 'setting'} coverage as secondary`,
          subText: err.message,
        })
      }
      setLoading(false)
    },
    [coverageId, showMessage, patientRef, isSecondary, appName],
  )

  // const [confirmSettingPrimary, setConfirmSettingPrimary] = useState(false)

  return (
    <>
      <Popover isLazy strategy="fixed" placement="right">
        <PopoverTrigger>
          <ActionButton pl={3} gap={1} bg="white" size="xs">
            <ArrowUpIcon w={4} h={4} />
            <Text>SET AS...</Text>
          </ActionButton>
        </PopoverTrigger>
        <PopoverContent py={2} px={3} w="260px">
          <Text lineHeight={1} fontWeight={600} mb={2} color="gray.600">
            Set coverage as...
          </Text>
          <Flex gap={2} w="100%" flexFlow="column">
            <SolidActionButton
              size="sm"
              bg={isPrimary ? 'red.500' : 'green.500'}
              onClick={() => handleSetAs(isPrimary ? null : 'primary')}
              isLoading={loading}>
              {isPrimary ? 'Unset as primary' : 'Primary'}
            </SolidActionButton>
            <SolidActionButton
              size="sm"
              bg={isSecondary ? 'red.500' : 'green.500'}
              onClick={() => handleSetAs(isSecondary ? null : 'secondary')}
              isLoading={loading}>
              {isSecondary ? 'Unset as secondary' : 'Secondary'}
            </SolidActionButton>
          </Flex>
          <PopoverArrow />
        </PopoverContent>
      </Popover>
      {/* {confirmSettingPrimary ? (
        <ConfirmSetPrimaryAlert onBack={() => setConfirmSettingPrimary(false)} />
      ) : null} */}
    </>
  )
}

const PregnancyCoverageSetAs = () => {
  const { coverageId, assessmentId, assessment } = useCoverageView()
  const { showMessage } = useContext(PopUpMessageContext)
  const [loading, setLoading] = useState(false)
  const handleSetPlanAs = useCallback(
    async (label: SetPregnancyCoverageLabelArgs['label']) => {
      if (!assessmentId) return
      try {
        setLoading(true)
        const {
          data: { success },
        } = await setPregnancyCoverageLabel({
          assessmentId,
          coverageId,
          label,
        })
        showMessage({
          type: 'success',
          text: 'Coverage updated',
          subText: success,
        })
      } catch (err: any) {
        showMessage({
          type: 'error',
          text: `Error setting coverage as ${label}`,
          subText: err.message,
        })
      }
      setLoading(false)
    },
    [coverageId, showMessage, assessmentId],
  )

  const canSetAs = useMemo<Array<SetPregnancyCoverageLabelArgs['label']>>(() => {
    if (!assessmentId || !assessment) return []
    if (assessment.primaryCoverageId === coverageId) return ['secondary', 'potential']
    if (assessment.secondaryCoverageId === coverageId) return ['primary', 'potential']
    return ['primary', 'secondary']
  }, [assessmentId, assessment, coverageId])

  const canUnset = useMemo(() => {
    if (!assessmentId || !assessment) return false
    if (assessment.primaryCoverageId === coverageId) return true
    if (assessment.secondaryCoverageId === coverageId) return true
    return false
  }, [assessmentId, assessment, coverageId])

  if (!canSetAs.length && !canUnset) return null

  return (
    <Popover isLazy strategy="fixed" placement="right">
      <PopoverTrigger>
        <ActionButton pl={3} gap={1} bg="white" size="xs">
          <ArrowUpIcon w={4} h={4} />
          <Text>SET AS...</Text>
        </ActionButton>
      </PopoverTrigger>
      <PopoverContent py={2} px={3} w="300px">
        <Text w="100%" textAlign="center" lineHeight={1} fontWeight={600} mb={2} color="gray.600">
          Set coverage as...
        </Text>
        <Flex gap={2} w="100%" flexFlow="column">
          {canSetAs.map(label =>
            label !== 'potential' ? (
              <SolidActionButton
                isLoading={loading}
                size="sm"
                key={label}
                onClick={() => handleSetPlanAs(label)}>
                {capitalizeFirstLetter(label)} coverage
              </SolidActionButton>
            ) : (
              <ActionButton size="sm" key={label} onClick={() => handleSetPlanAs(label)}>
                {capitalizeFirstLetter(label)} coverage
              </ActionButton>
            ),
          )}
        </Flex>
        <PopoverArrow />
      </PopoverContent>
    </Popover>
  )
}

const CoverageSetAs = () => {
  const { assessmentId } = useCoverageView()

  if (assessmentId) return <PregnancyCoverageSetAs />
  return <PatientCoverageSetAs />
}

export const CoverageActions = ({ adminView }: { adminView: boolean | undefined }) => {
  const { assessments } = useProfile()
  const { patientRef, coverageId, coverage, assessmentId, assessment } = useCoverageView()
  const { showMessage } = useContext(PopUpMessageContext)
  // const shouldDisplay = useMemo(() => {
  //   if (coverageId === 'primary' && !assessmentId) return false
  //   // if (!assessment?.plans?.primaryIsMedicaid && assessment?.nonMedicaidCoverageId === coverageId)
  //   //   return false
  //   // if (!!assessment?.plans?.primaryIsMedicaid && assessment?.medicaidCoverageId === coverageId)
  //   //   return false
  //   return true
  // }, [coverageId, assessmentId])

  const handleDelete = useCallback(async () => {
    try {
      if (assessmentId) {
        const batch = writeBatch(db)
        const assessmentRef = doc(db, `${ASSESSMENTS}/${assessmentId}`)
        if (assessment?.primaryCoverageId === coverageId) {
          batch.update(assessmentRef, 'primaryCoverageId', null)
        }
        if (assessment?.secondaryCoverageId === coverageId) {
          batch.update(assessmentRef, 'secondaryCoverageId', null)
        }
        if (assessment?.potentialCoverageIds?.includes(coverageId)) {
          batch.update(assessmentRef, 'potentialCoverageIds', arrayRemove(coverageId))
        }
        await batch.commit()
      } else {
        const adminRef = doc(db, `${USERS_ADMIN}/${patientRef.id}`)
        const batch = writeBatch(db)
        batch.update(patientRef, getCoveragePath(coverageId), deleteField())
        const nextActionPath = getPlanNextActionPath(coverageId)
        batch.update(adminRef, nextActionPath, deleteField())
        await batch.commit()
      }
    } catch (err: any) {
      showMessage({
        type: 'error',
        text: 'Error deleting coverage',
        subText: err.message,
      })
    }
  }, [patientRef, coverageId, assessmentId, assessment, showMessage])

  const linkedPregnancies = useMemo<Array<WithId<Assessment>>>(
    () =>
      coverage
        ? Object.entries(assessments ?? {})
            .filter(([, a]) => pregnancyHasPlan(a, coverage.id))
            .map(([id, a]) => ({ ...a, id }))
        : [],
    [assessments, coverage],
  )

  // if (!shouldDisplay) return null
  return (
    <Flex align="center" w="100%" px={2} py={1}>
      {adminView ? <CoverageSetAs /> : null}
      <Box ml="auto">
        <DeleteButton
          text={assessmentId ? 'Unlink Pregnancy' : 'Delete Coverage'}
          size="sm"
          deleteDisabled={!!linkedPregnancies.length && !assessmentId}
          actionName={assessmentId ? 'Unlink' : 'Delete'}
          icon={assessmentId ? <MinusIcon w={4} h={3} /> : <DeleteIcon />}
          alertBody={
            assessmentId ? (
              <UnlinkFromPregnancyAlertBody />
            ) : (
              <DeleteFromPatientAlertBody linkedPregnancies={linkedPregnancies} />
            )
          }
          itemName="coverage"
          onDelete={() => handleDelete()}
        />
      </Box>
    </Flex>
  )
}
